import { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import axios from "axios";
import { LOGOUTURL } from "../../DataStore/ApiConstant";

const DEFAULT_INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes
const INACTIVITY_MS = (() => {
  const fromEnv = Number(process.env.REACT_APP_INACTIVITY_MS);
  return Number.isFinite(fromEnv) && fromEnv > 0 ? fromEnv : DEFAULT_INACTIVITY_MS;
})();
const LAST_ACTIVITY_KEY = "lastActivityTs";

const InactivityLogout = ({ dispatchUserLogout }) => {
  const history = useHistory();

  const logoutTimeoutRef = useRef(null);
  const safetyIntervalRef = useRef(null);
  const loginPollIntervalRef = useRef(null);
  const isLoggingOutRef = useRef(false);
  const lastWriteRef = useRef(0);

  const isLoggedIn = () => Boolean(localStorage.getItem("login"));

  const clearTimers = useCallback(() => {
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (safetyIntervalRef.current) {
      clearInterval(safetyIntervalRef.current);
      safetyIntervalRef.current = null;
    }
  }, []);

  const doLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    if (!isLoggedIn()) return;

    isLoggingOutRef.current = true;

    try {
      await axios.get(LOGOUTURL);
    } catch (_) {
      // ignore network errors; still clear local session
    } finally {
      localStorage.removeItem("login");
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      dispatchUserLogout();
      clearTimers();
      history.replace("/");
      window.location.reload();
      isLoggingOutRef.current = false;
    }
  }, [clearTimers, dispatchUserLogout, history]);

  const scheduleLogoutFromTs = useCallback(
    (lastActivityTs) => {
      clearTimers();
      if (!isLoggedIn()) return;

      const remaining = lastActivityTs + INACTIVITY_MS - Date.now();
      if (remaining <= 0) {
        doLogout();
        return;
      }

      logoutTimeoutRef.current = setTimeout(() => {
        doLogout();
      }, remaining);

      safetyIntervalRef.current = setInterval(() => {
        const tsStr = localStorage.getItem(LAST_ACTIVITY_KEY);
        const ts = tsStr ? Number(tsStr) : 0;
        if (!ts) return;
        if (Date.now() - ts >= INACTIVITY_MS) {
          doLogout();
        }
      }, 30 * 1000);
    },
    [clearTimers, doLogout]
  );

  const markActivity = useCallback(() => {
    if (!isLoggedIn()) return;

    const now = Date.now();
    if (now - lastWriteRef.current < 1000) {
      const tsStr = localStorage.getItem(LAST_ACTIVITY_KEY);
      const ts = tsStr ? Number(tsStr) : now;
      scheduleLogoutFromTs(ts);
      return;
    }

    lastWriteRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    scheduleLogoutFromTs(now);
  }, [scheduleLogoutFromTs]);

  useEffect(() => {
    const ensureSessionTracking = () => {
      if (!isLoggedIn()) {
        clearTimers();
        return;
      }

      const tsStr = localStorage.getItem(LAST_ACTIVITY_KEY);
      const ts = tsStr ? Number(tsStr) : 0;

      // If this is a fresh login or a stale timestamp from a previous session,
      // reset activity so we don't immediately log out.
      if (!ts || Date.now() - ts >= INACTIVITY_MS) {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      }

      const newTs = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
      scheduleLogoutFromTs(newTs);
    };

    ensureSessionTracking();

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click",
    ];

    activityEvents.forEach((evt) => {
      window.addEventListener(evt, markActivity, { passive: true });
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        markActivity();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const onStorage = (e) => {
      if (e.key === "login" && e.newValue === null) {
        dispatchUserLogout();
        clearTimers();
        history.replace("/");
      }
      if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        const ts = Number(e.newValue);
        if (!Number.isNaN(ts)) scheduleLogoutFromTs(ts);
      }
    };
    window.addEventListener("storage", onStorage);

    loginPollIntervalRef.current = setInterval(() => {
      ensureSessionTracking();
    }, 2000);

    return () => {
      activityEvents.forEach((evt) => {
        window.removeEventListener(evt, markActivity);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("storage", onStorage);
      if (loginPollIntervalRef.current) {
        clearInterval(loginPollIntervalRef.current);
        loginPollIntervalRef.current = null;
      }
      clearTimers();
    };
  }, [clearTimers, dispatchUserLogout, history, markActivity, scheduleLogoutFromTs]);

  return null;
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatchUserLogout: () => dispatch({ type: "USER_LOGOUT" }),
  };
};

export default connect(null, mapDispatchToProps)(InactivityLogout);
